import { useState } from 'react'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import Card from '../components/common/Card'
import PageContainer from '../components/layout/PageContainer'
import Button from '../components/common/Button'
import ConfirmDialog from '../components/common/ConfirmDialog'
import Dialog from '../components/common/Dialog'

const ApiManagement = () => {
  const [apiKeys, setApiKeys] = useState([
    {
      id: 1,
      key: 'sk_live_xxxxxxxxxxxxxxxxxxxxx',
      type: 'Production',
      created: 'Nov 1, 2025',
      visible: false,
    },
    {
      id: 2,
      key: 'sk_test_xxxxxxxxxxxxxxxxxxxxx',
      type: 'Test',
      created: 'Oct 15, 2025',
      visible: false,
    },
  ])
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [showRevokeDialog, setShowRevokeDialog] = useState(null)
  const [newKeyType, setNewKeyType] = useState('production')

  const handleCopy = (key) => {
    navigator.clipboard.writeText(key)
    toast.success('API key copied to clipboard!')
  }

  const handleRegenerate = (id) => {
    setApiKeys((keys) =>
      keys.map((k) =>
        k.id === id
          ? {
            ...k,
            key: `${k.type === 'Production' ? 'sk_live' : 'sk_test'}_${Math.random().toString(36).substring(2, 15)}`,
          }
          : k
      )
    )
    toast.success('API key regenerated successfully!')
  }

  const toggleKeyVisibility = (id) => {
    setApiKeys((keys) => keys.map((k) => (k.id === id ? { ...k, visible: !k.visible } : k)))
  }

  const handleGenerate = () => {
    const newKey = {
      id: Date.now(),
      key: `${newKeyType === 'production' ? 'sk_live' : 'sk_test'}_${Math.random().toString(36).substring(2, 15)}`,
      type: newKeyType === 'production' ? 'Production' : 'Test',
      created: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      visible: false,
    }
    setApiKeys([newKey, ...apiKeys])
    setShowGenerateDialog(false)
    toast.success('New API key generated successfully!')
  }

  const handleRevoke = (id) => {
    setApiKeys((keys) => keys.filter((k) => k.id !== id))
    setShowRevokeDialog(null)
    toast.success('API key revoked successfully!')
  }

  return (
    <PageContainer title="API Management" subtitle="Manage your API keys and integrations">
      <Card title="API Keys" className="mb-6">
        <Button
          icon="mdi:plus"
          className="mb-4 w-full sm:w-auto"
          size="sm"
          onClick={() => setShowGenerateDialog(true)}
        >
          <span className="hidden xs:inline">Generate New API Key</span>
          <span className="xs:hidden">Generate Key</span>
        </Button>

        <div className="space-y-3 sm:space-y-4">
          {apiKeys.map((apiKey) => (
            <div
              key={apiKey.id}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-3 sm:p-4 bg-bg-light rounded-lg"
            >
              <div className="flex-1 min-w-0 w-full sm:w-auto">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <code className="font-mono text-xs sm:text-sm bg-white px-2 py-1.5 sm:py-1 rounded border border-border-light break-all flex-1 min-w-0">
                    {apiKey.visible ? apiKey.key : '•'.repeat(Math.min(apiKey.key.length, 30))}
                  </code>
                  <button
                    onClick={() => toggleKeyVisibility(apiKey.id)}
                    className="text-text-secondary hover:text-primary transition-colors flex-shrink-0 p-1"
                    aria-label={apiKey.visible ? 'Hide key' : 'Show key'}
                  >
                    <Icon icon={apiKey.visible ? 'mdi:eye-off' : 'mdi:eye'} className="text-lg sm:text-xl" />
                  </button>
                </div>
                <p className="text-xs sm:text-sm text-text-secondary break-words">
                  <span className="font-medium">{apiKey.type} Key</span>
                  <span className="hidden sm:inline"> • </span>
                  <span className="block sm:inline">Created on {apiKey.created}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 sm:flex-shrink-0 w-full sm:w-auto">
                {/* Mobile: Full width Revoke button with icon buttons */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowRevokeDialog(apiKey.id)}
                  className="flex-1 sm:flex-none"
                >
                  Revoke
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="mdi:content-copy"
                  onClick={() => handleCopy(apiKey.key)}
                  className="sm:hidden"
                  aria-label="Copy API key"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  icon="mdi:refresh"
                  onClick={() => handleRegenerate(apiKey.id)}
                  className="sm:hidden"
                  aria-label="Regenerate API key"
                />
                {/* Desktop: All action buttons */}
                <Button
                  variant="secondary"
                  size="sm"
                  icon="mdi:content-copy"
                  onClick={() => handleCopy(apiKey.key)}
                  className="hidden sm:flex"
                  aria-label="Copy API key"
                >
                  <span className="hidden md:inline">Copy</span>
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="mdi:refresh"
                  onClick={() => handleRegenerate(apiKey.id)}
                  className="hidden sm:flex"
                  aria-label="Regenerate API key"
                >
                  <span className="hidden md:inline">Regenerate</span>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="API Documentation" className="mb-6">
        <div className="flex gap-4 flex-wrap">
          <a
            href="#"
            className="flex items-center gap-2 px-4 py-2 bg-bg-light rounded-lg text-indigo-600 hover:bg-indigo-600/10 transition-colors font-semibold"
          >
            <Icon icon="mdi:book" />
            <span>Getting Started Guide</span>
          </a>
          <a
            href="#"
            className="flex items-center gap-2 px-4 py-2 bg-bg-light rounded-lg text-indigo-600 hover:bg-indigo-600/10 transition-colors font-semibold"
          >
            <Icon icon="mdi:code-tags" />
            <span>API Reference</span>
          </a>
          <a
            href="#"
            className="flex items-center gap-2 px-4 py-2 bg-bg-light rounded-lg text-indigo-600 hover:bg-indigo-600/10 transition-colors font-semibold"
          >
            <Icon icon="mdi:rocket-launch" />
            <span>Example Projects</span>
          </a>
        </div>
      </Card>

      <Card title="API Usage">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div className="p-4 bg-bg-light rounded-lg">
            <p className="text-sm text-text-secondary mb-1">Requests Today</p>
            <p className="text-2xl font-bold text-text-dark">1,234</p>
          </div>
          <div className="p-4 bg-bg-light rounded-lg">
            <p className="text-sm text-text-secondary mb-1">Requests This Month</p>
            <p className="text-2xl font-bold text-text-dark">45,678</p>
          </div>
          <div className="p-4 bg-bg-light rounded-lg">
            <p className="text-sm text-text-secondary mb-1">Rate Limit</p>
            <p className="text-2xl font-bold text-text-dark">50/min</p>
          </div>
        </div>
      </Card>

      {/* Generate API Key Dialog */}
      <Dialog
        isOpen={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        title="Generate New API Key"
        size="sm"
        footer={
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 w-full">
            <Button
              variant="secondary"
              onClick={() => setShowGenerateDialog(false)}
              className="w-full sm:w-auto"
              size="sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              icon="mdi:key"
              className="w-full sm:w-auto"
              size="sm"
            >
              Generate Key
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">Select the type of API key to generate:</p>
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 border border-border-light rounded-lg cursor-pointer hover:bg-bg-light">
              <input
                type="radio"
                name="keyType"
                value="production"
                checked={newKeyType === 'production'}
                onChange={(e) => setNewKeyType(e.target.value)}
                className="w-4 h-4"
              />
              <div>
                <span className="font-medium">Production Key</span>
                <p className="text-xs text-text-secondary">For live applications</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 border border-border-light rounded-lg cursor-pointer hover:bg-bg-light">
              <input
                type="radio"
                name="keyType"
                value="test"
                checked={newKeyType === 'test'}
                onChange={(e) => setNewKeyType(e.target.value)}
                className="w-4 h-4"
              />
              <div>
                <span className="font-medium">Test Key</span>
                <p className="text-xs text-text-secondary">For development and testing</p>
              </div>
            </label>
          </div>
        </div>
      </Dialog>

      {/* Revoke API Key Confirmation */}
      <ConfirmDialog
        isOpen={showRevokeDialog !== null}
        onClose={() => setShowRevokeDialog(null)}
        onConfirm={() => handleRevoke(showRevokeDialog)}
        title="Revoke API Key"
        message="Are you sure you want to revoke this API key? This action cannot be undone and any applications using this key will stop working."
        confirmText="Revoke"
        cancelText="Cancel"
        variant="danger"
        icon="mdi:key-remove"
      />
    </PageContainer>
  )
}

export default ApiManagement
